import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { toast } from "sonner";
import { devLog } from "@/lib/devLog";

/**
 * Bluetooth thermal-label printer integration (Beeprt BY-243BT and similar
 * TSPL/TSC-based 58 mm label printers).
 *
 * We stay browser-only: Web Bluetooth talks to the printer's GATT and we
 * stream raw TSPL command bytes to whichever write characteristic the
 * device exposes. The bakery's BY-243BT advertises a Nordic-UART-like
 * profile so we probe the well-known printer/serial service UUIDs in order
 * and use the first writable characteristic we find.
 *
 * Status model:
 *   - "unsupported": browser has no Web Bluetooth (iPad Safari, etc.).
 *   - "disconnected": no GATT connection.
 *   - "connecting": user picked a device, GATT handshake in progress.
 *   - "connected": ready to print.
 *
 * Printing falls back to `window.print()` when the printer isn't connected,
 * so the existing browser-print workflow still works on un-paired tablets.
 */
const PrinterCtx = createContext(null);

// Known GATT services exposed by cheap BLE thermal printers (Beeprt,
// Phomemo, Munbyn, Itari, generic ESC/POS+TSPL devices). We list them as
// optionalServices so the chooser can surface ANY device, then we walk
// them in order looking for a writable characteristic.
//
// IMPORTANT: Web Bluetooth only lets us read services whose UUIDs we
// declared up front in optionalServices — there is no wildcard "give me
// every service" call. So we cast a wide net here covering every UUID
// observed on Beeprt / Phomemo / Munbyn / Itari / Xprinter / Rongta /
// Goojprt / generic ESC/POS+TSPL BLE printers in the wild.
const CANDIDATE_SERVICES = [
  // Beeprt BY-243BT — confirmed custom Nordic-UART variant
  "8e400001-f315-4f60-9fb8-838830daea50",
  // Nordic UART (most Beeprt / generic BLE label printers)
  "6e400001-b5a3-f393-e0a9-e50e24dcca9e",
  // Generic SPP-style printer service
  "000018f0-0000-1000-8000-00805f9b34fb",
  // HM-10 style serial (very common Chinese BLE modules)
  "0000ffe0-0000-1000-8000-00805f9b34fb",
  // Alt printer profile (Goojprt, generic 58 mm clones)
  "0000ff00-0000-1000-8000-00805f9b34fb",
  // ISSC / Microchip transparent UART (some Beeprt firmwares)
  "49535343-fe7d-4ae5-8fa9-9fafd205e455",
  // BY-243BT / Phomemo / Munbyn label printer service
  "0000fff0-0000-1000-8000-00805f9b34fb",
  // Some Beeprt firmwares advertise this for the print channel
  "0000ff12-0000-1000-8000-00805f9b34fb",
  // Espruino / generic BLE serial
  "e7810a71-73ae-499d-8c15-faa9aef0c3f2",
  // Xiaomi-style mesh, occasionally borrowed by cheap printers
  "0000fee7-0000-1000-8000-00805f9b34fb",
  // Rongta / Xprinter alternate service
  "0000ae30-0000-1000-8000-00805f9b34fb",
  // ISSC alt variant seen on Beeprt clones
  "49535343-1e4d-4bd9-ba61-23c647249616",
];

const DEVICE_HINT_KEY = "ozbakery.printer.hint";
const LABEL_SIZE_KEY = "ozbakery.printer.label_size";
const FONT_OVERRIDES_KEY = "ozbakery.printer.font_overrides";
const isPrinter = (s) => s === "connected";

// Text-size options. Cheap TSPL printers (Beeprt BY-243BT included)
// only ship bitmap fonts 1–3 reliably; fonts 4/5 are silently ignored
// or fall back to default size. We scale text the *portable* way: pick
// a base font + the TSPL `TEXT` command's X/Y multipliers, which every
// TSPL device honours.
//
// `id` round-trips through localStorage as the operator's choice.
// `font` is the TSPL bitmap font we emit (always 1–3 for compatibility).
// `mul` is the X/Y multiplier we pass to the TEXT command — `1` is the
// font's native size, `2` doubles every glyph dimension, etc.
// `dots` is the resulting on-paper height (font height × multiplier),
// used by both the preview and `layoutFor` to calculate row spacing.
export const TSPL_FONTS = [
  { id: "xs", label: "Extra small", font: "1", mul: 1, dots: 20 },
  { id: "sm", label: "Small", font: "2", mul: 1, dots: 24 },
  { id: "md", label: "Medium", font: "3", mul: 1, dots: 32 },
  { id: "lg", label: "Large", font: "3", mul: 2, dots: 64 },
  { id: "xl", label: "Extra-large", font: "3", mul: 3, dots: 96 },
  { id: "xxl", label: "Huge", font: "3", mul: 4, dots: 128 },
];
const FONT_BY_ID = Object.fromEntries(TSPL_FONTS.map((f) => [f.id, f]));
// Look-up shortcut for places that only need glyph height in dots.
const fontDots = (id) => FONT_BY_ID[id]?.dots ?? 24;

// Common BLE thermal-label sizes the bakery is likely to load. Width and
// height are in millimetres; `gap` is the inter-label gap (also mm). The
// last item is treated as the "custom" sentinel so the popover can swap to
// numeric inputs.
export const LABEL_SIZE_PRESETS = [
  { id: "40x30", label: "40 × 30 mm", width: 40, height: 30, gap: 2 },
  { id: "50x30", label: "50 × 30 mm", width: 50, height: 30, gap: 2 },
  { id: "50x40", label: "50 × 40 mm", width: 50, height: 40, gap: 2 },
  { id: "50x80", label: "50 × 80 mm", width: 50, height: 80, gap: 3 },
  { id: "60x40", label: "60 × 40 mm", width: 60, height: 40, gap: 2 },
  { id: "100x50", label: "100 × 50 mm", width: 100, height: 50, gap: 3 },
  { id: "100x75", label: "100 × 75 mm", width: 100, height: 75, gap: 3 },
  { id: "custom", label: "Custom…", width: 50, height: 30, gap: 2, custom: true },
];

const DEFAULT_LABEL_SIZE = { width: 50, height: 30, gap: 2 };
// Empty overrides == "use the auto tier-based pick". Storing a font id
// (e.g. "3") here pins that line to a specific TSPL font.
const DEFAULT_FONT_OVERRIDES = { title: null, product: null, detail: null };

function readStoredLabelSize() {
  try {
    const raw = localStorage.getItem(LABEL_SIZE_KEY);
    if (!raw) return DEFAULT_LABEL_SIZE;
    const parsed = JSON.parse(raw);
    if (
      typeof parsed?.width === "number" &&
      typeof parsed?.height === "number" &&
      typeof parsed?.gap === "number"
    ) {
      return parsed;
    }
    return DEFAULT_LABEL_SIZE;
  } catch {
    return DEFAULT_LABEL_SIZE;
  }
}

function readStoredFontOverrides() {
  try {
    const raw = localStorage.getItem(FONT_OVERRIDES_KEY);
    if (!raw) return DEFAULT_FONT_OVERRIDES;
    const parsed = JSON.parse(raw);
    // Only accept ids we know about so a stale storage value can't crash
    // the layout engine with an undefined font height.
    const sanitize = (v) => (v && FONT_BY_ID[v] ? v : null);
    return {
      title: sanitize(parsed?.title),
      product: sanitize(parsed?.product),
      detail: sanitize(parsed?.detail),
    };
  } catch {
    return DEFAULT_FONT_OVERRIDES;
  }
}

const encode = (str) => new TextEncoder().encode(str);

// Chunk writes — BLE MTU is 20 bytes by default and 100 is a safe upper
// bound across firmwares without negotiating an MTU upgrade.
const CHUNK = 100;
async function writeChunked(characteristic, bytes) {
  for (let i = 0; i < bytes.length; i += CHUNK) {
    const slice = bytes.slice(i, i + CHUNK);
    // writeValueWithoutResponse is faster but not all firmwares allow it.
    // Fall back to writeValue when the property isn't supported.
    if (characteristic.properties?.writeWithoutResponse) {
      await characteristic.writeValueWithoutResponse(slice);
    } else {
      await characteristic.writeValue(slice);
    }
  }
}

/**
 * Compute a layout that genuinely fills the label.
 *
 * TSPL ships a fixed bitmap font set; the height of each font in dots
 * (203 DPI) is roughly:
 *   "1" = 20, "2" = 24, "3" = 32, "4" = 48, "5" = 72
 *
 * We pick a title / product-name / detail-line font from the label's
 * physical width — wider labels get bigger text — then stack the lines
 * top-down and reserve the bottom ~38 % of the label for the barcode.
 * Font heights drive lineGap so adding/removing a line never overlaps.
 *
 * Inputs are mm, internal numbers are dots. The printer truncates if we
 * over-shoot, but with these proportions a 50 × 80 label uses every dot.
 */
// Tier configuration table. Picking values from a static object keeps
// `layoutFor()` linear (no nested ternaries) and means every tier lives
// in one place — easier to tune when a new label format shows up.
//
//   autoFonts: size-id picks for title / product / detail given the tier
//   bar: TSPL barcode narrow/wide multiplier — bigger labels get a
//        chunkier barcode that scans from across the kitchen
//   share: share of label height reserved for the barcode (0–1)
//   gap:   per-line spacing function — "sm" labels use tight fixed dots,
//          larger labels scale spacing proportional to glyph height
const TIER_CONFIG = {
  lg: {
    autoFonts: (w) => ({ title: "md", product: w >= 90 ? "xl" : "lg", detail: "sm" }),
    bar: 4,
    share: 0.34,
    gap: (h) => Math.floor(h * 0.3),
    headSpace: (h) => Math.floor(h * 0.35),
    productSpace: (h) => Math.floor(h * 0.25),
  },
  md: {
    autoFonts: (w) => ({ title: "sm", product: w >= 80 ? "lg" : "md", detail: "xs" }),
    bar: 3,
    share: 0.36,
    gap: (h) => Math.floor(h * 0.3),
    headSpace: (h) => Math.floor(h * 0.35),
    productSpace: (h) => Math.floor(h * 0.25),
  },
  sm: {
    autoFonts: (w) => ({ title: "xs", product: w >= 80 ? "md" : "sm", detail: "xs" }),
    bar: 2,
    share: 0.4,
    gap: () => 3,
    headSpace: () => 4,
    productSpace: () => 4,
  },
};

function pickTier(widthMm, heightMm) {
  if (heightMm >= 60 && widthMm >= 50) return "lg";
  if (heightMm >= 38 && widthMm >= 50) return "md";
  return "sm";
}

export function layoutFor(widthMm, heightMm, fontOverrides = DEFAULT_FONT_OVERRIDES) {
  const W = widthMm * 8;
  const H = heightMm * 8;
  const margin = Math.max(8, Math.floor(W * 0.03));

  const tier = pickTier(widthMm, heightMm);
  const cfg = TIER_CONFIG[tier];

  // Resolve the operator's overrides over the auto picks. Each role
  // ends up holding a size id (e.g. "lg") which then looks up in
  // FONT_BY_ID to get the actual TSPL font + multiplier.
  const auto = cfg.autoFonts(widthMm);
  const ids = {
    title: fontOverrides?.title || auto.title,
    product: fontOverrides?.product || auto.product,
    detail: fontOverrides?.detail || auto.detail,
  };
  const titleSpec = FONT_BY_ID[ids.title] || FONT_BY_ID.sm;
  const productSpec = FONT_BY_ID[ids.product] || FONT_BY_ID.md;
  const detailSpec = FONT_BY_ID[ids.detail] || FONT_BY_ID.xs;
  const tH = titleSpec.dots;
  const pH = productSpec.dots;
  const dH = detailSpec.dots;

  const titleY = margin;
  const productY = titleY + tH + cfg.headSpace(tH);
  const detailStart = productY + pH + cfg.productSpace(pH);
  const lineGap = dH + cfg.gap(dH);

  // Barcode height: share of label height capped so very tall labels
  // don't produce a ridiculous slab. Shrink to fit if the text block
  // ran long — readability of the product name beats the barcode.
  let barcodeH = Math.max(48, Math.min(280, Math.floor(H * cfg.share)));
  const textBlockEnd = detailStart + lineGap * 4;
  const available = H - textBlockEnd - margin;
  if (available < barcodeH) barcodeH = Math.max(40, available - 4);
  const barcodeY = Math.max(textBlockEnd + 6, H - barcodeH - margin);

  return {
    margin,
    titleId: ids.title,
    productId: ids.product,
    detailId: ids.detail,
    titleFont: titleSpec.font,
    titleMul: titleSpec.mul,
    productFont: productSpec.font,
    productMul: productSpec.mul,
    detailFont: detailSpec.font,
    detailMul: detailSpec.mul,
    titleY,
    productY,
    detailStart,
    lineGap,
    barcodeY,
    barcodeH,
    barNarrow: cfg.bar,
    barWide: cfg.bar,
    showAllergens: heightMm >= 35,
    productMaxChars: Math.max(6, Math.floor((W - margin * 2) / (pH * 0.55))),
    detailMaxChars: Math.max(10, Math.floor((W - margin * 2) / (dH * 0.55))),
  };
}

const sizeHeader = (size) => [
  `SIZE ${size.width} mm,${size.height} mm`,
  `GAP ${size.gap} mm,0 mm`,
  "DIRECTION 1",
  "CLS",
];

// TSPL is single-quote intolerant inside double-quoted strings; collapse
// any quotes the user typed in a recipe name to a safe character.
const safe = (s, n) => String(s ?? "").slice(0, n).replace(/"/g, "'");

/**
 * Plan the bottom-of-label barcode region.
 *
 * We tried QR earlier — the BY-243BT firmware silently rejects TSPL
 * `QRCODE` and prints the literal command text on the label, which
 * pushed content onto a second label. So we use only Code 128: the
 * EY-034 scanner decodes it reliably once the *narrow* dimension is
 * ≥ 3 dots (0.375 mm at 203 DPI). We force narrow ≥ 3, then auto-shrink
 * the barcode height/data padding if the encoded width overflows.
 *
 * Returns positions in dots:
 *   code128X, code128Y: top-left of the bars
 *   code128W:           computed encoded width (informational, used by
 *                       the preview)
 *   narrow / wide:      multipliers for the TSPL BARCODE command
 *   showHumanText:      whether to render the code as text below the
 *                       bars (always true — gives the operator a
 *                       fallback if the scanner ever fails)
 */
export function planBarcodes(L, widthMm, dataLen) {
  const W = widthMm * 8;
  const available = W - L.margin * 2;
  // Smallest scannable bar width for EY-034 = 3 dots; clamp upwards
  // until the encoded width overflows the label, then step back.
  let narrow = Math.max(3, L.barNarrow);
  const estWidth = (n) => dataLen * 13 * n;
  while (narrow > 2 && estWidth(narrow) > available) narrow -= 1;
  // If we couldn't fit it even at narrow=2, leave at 2 — printer will
  // truncate/scale and at least the bars will be on the label.
  return {
    mode: "code128",
    code128X: L.margin,
    code128Y: L.barcodeY,
    code128W: Math.min(available, estWidth(narrow)),
    narrow,
    wide: narrow,
    showHumanText: true,
  };
}

// Emit TSPL for the barcode region. The 5th BARCODE arg is the
// "human-readable" flag: 1 = bars + code text underneath, which gives
// floor staff a manual-typing fallback when scanners misbehave.
function barcodeCommands(plan, L, data) {
  return [
    `BARCODE ${plan.code128X},${plan.code128Y},"128",${L.barcodeH},${plan.showHumanText ? 1 : 0},0,${plan.narrow},${plan.wide},"${data}"`,
  ];
}

/**
 * Build a TSPL command stream for an Oz Bakery product label.
 * Layout scales to whatever width × height the operator picked in the
 * printer popover.
 */
function buildPackingLabelTSPL(p, size, fontOverrides) {
  const L = layoutFor(size.width, size.height, fontOverrides);
  // Helpers so each TEXT line stays readable. The 5th & 6th args are the
  // X / Y multipliers — that's what actually scales glyphs on cheap TSPL
  // printers that ignore font ids 4-5.
  const t = (y, text) =>
    `TEXT ${L.margin},${y},"${L.titleFont}",0,${L.titleMul},${L.titleMul},"${text}"`;
  const pr = (y, text) =>
    `TEXT ${L.margin},${y},"${L.productFont}",0,${L.productMul},${L.productMul},"${text}"`;
  const d = (y, text) =>
    `TEXT ${L.margin},${y},"${L.detailFont}",0,${L.detailMul},${L.detailMul},"${text}"`;

  const lines = [
    ...sizeHeader(size),
    t(L.titleY, "Oz Bakery"),
    pr(L.productY, safe(p.recipe_name, L.productMaxChars)),
    d(L.detailStart, `Batch: ${safe(p.batch_code, L.detailMaxChars - 7)}`),
    d(L.detailStart + L.lineGap, `Packs: ${p.packs_produced || ""} ${safe(p.pack_unit, 6)}`),
    d(L.detailStart + L.lineGap * 2, `Packed: ${safe(p.packed_at, 10)}`),
  ];
  if (L.showAllergens && p.allergens?.length) {
    lines.push(
      d(L.detailStart + L.lineGap * 3, `Contains: ${safe(p.allergens.join(","), L.detailMaxChars - 10)}`),
    );
  }
  // Dual-symbology barcode region — see planBarcodes() rationale.
  const plan = planBarcodes(L, size.width, String(p.label_code || "").length);
  lines.push(...barcodeCommands(plan, L, p.label_code));
  lines.push("PRINT 1,1", "");
  return lines.join("\r\n");
}

/**
 * Build a TSPL stream for a goods-intake raw-material label.
 */
function buildIntakeLabelTSPL(p, size, fontOverrides) {
  const L = layoutFor(size.width, size.height, fontOverrides);
  const t = (y, text) =>
    `TEXT ${L.margin},${y},"${L.titleFont}",0,${L.titleMul},${L.titleMul},"${text}"`;
  const pr = (y, text) =>
    `TEXT ${L.margin},${y},"${L.productFont}",0,${L.productMul},${L.productMul},"${text}"`;
  const d = (y, text) =>
    `TEXT ${L.margin},${y},"${L.detailFont}",0,${L.detailMul},${L.detailMul},"${text}"`;

  // Build the detail-row list dynamically — skip any field the operator
  // didn't fill in so the label doesn't show "Sup:" / "BBE:" with
  // nothing after them. The order below is what shows up on paper top-
  // to-bottom; the supplier name goes first because it's the most
  // useful at-a-glance identifier in the cold-room.
  const detailRows = [];
  if (p.supplier_name) {
    detailRows.push(`From: ${safe(p.supplier_name, L.detailMaxChars - 6)}`);
  }
  detailRows.push(`Int: ${safe(p.internal_batch_code, L.detailMaxChars - 5)}`);
  if (p.supplier_batch) {
    detailRows.push(`Sup: ${safe(p.supplier_batch, L.detailMaxChars - 5)}`);
  }
  if (p.expiry_date) {
    detailRows.push(`BBE: ${safe(p.expiry_date, 10)}`);
  }
  if (p.received_at) {
    detailRows.push(`Rcvd: ${safe(p.received_at, 10)}`);
  }
  if (p.quantity) {
    detailRows.push(`Qty: ${p.quantity} ${safe(p.unit, 6)}`);
  }
  if (p.pack_count && p.pack_size) {
    detailRows.push(
      `Pack: ${p.pack_count} x ${p.pack_size}${safe(p.pack_unit, 4)}`,
    );
  }
  if (L.showAllergens && p.allergens?.length) {
    detailRows.push(`Allerg: ${safe(p.allergens.join(","), L.detailMaxChars - 8)}`);
  }

  const lines = [
    ...sizeHeader(size),
    t(L.titleY, "Oz Bakery · Intake"),
    pr(L.productY, safe(p.material_name, L.productMaxChars)),
    ...detailRows.map((text, i) => d(L.detailStart + L.lineGap * i, text)),
  ];

  lines.push(
    ...barcodeCommands(
      planBarcodes(L, size.width, String(p.internal_batch_code || "").length),
      L,
      p.internal_batch_code,
    ),
    "PRINT 1,1",
    "",
  );
  return lines.join("\r\n");
}

const wrapText = (text, n) => {
  const out = [];
  String(text || "").split(/\r?\n/).forEach((para) => {
    let line = "";
    para.split(/\s+/).filter(Boolean).forEach((w) => {
      while (w.length > n) {
        if (line) { out.push(line); line = ""; }
        out.push(w.slice(0, n));
        w = w.slice(n);
      }
      if ((line ? line.length + 1 : 0) + w.length > n) { out.push(line); line = w; }
      else line = line ? `${line} ${w}` : w;
    });
    if (line) out.push(line);
  });
  return out;
};

/**
 * Recipe ingredient list (+ allergens + recipe notes). Spills onto as many
 * labels as needed; continuation labels are marked "(2/3)" etc.
 */
export function buildRecipeLabelTSPL(r, size, fontOverrides) {
  const L = layoutFor(size.width, size.height, fontOverrides);
  const H = size.height * 8;
  const n = L.detailMaxChars;
  const rows = [];
  if (r.batch_code) rows.push(safe(`Batch ${r.batch_code} · ${r.planned ?? ""} ${r.yield_unit || ""}`, n));
  (r.ingredients || []).forEach((i) =>
    wrapText(`${i.qty}${i.unit || ""} ${i.name}`, n).forEach((t) => rows.push(safe(t, n))),
  );
  if (r.allergens?.length) wrapText(`Allergens: ${r.allergens.join(", ")}`, n).forEach((t) => rows.push(safe(t, n)));
  if (r.notes?.trim()) {
    rows.push("Notes:");
    wrapText(r.notes, n).forEach((t) => rows.push(safe(t, n)));
  }
  const perPage = Math.max(1, Math.floor((H - L.detailStart - L.margin) / L.lineGap));
  const pages = [];
  for (let i = 0; i < rows.length; i += perPage) pages.push(rows.slice(i, i + perPage));
  if (!pages.length) pages.push([]);
  const out = [];
  pages.forEach((pageRows, pi) => {
    const tag = pages.length > 1 ? ` (${pi + 1}/${pages.length})` : "";
    out.push(
      ...sizeHeader(size),
      `TEXT ${L.margin},${L.titleY},"${L.titleFont}",0,${L.titleMul},${L.titleMul},"Oz Bakery · Recipe${tag}"`,
      `TEXT ${L.margin},${L.productY},"${L.productFont}",0,${L.productMul},${L.productMul},"${safe(r.name, L.productMaxChars)}"`,
      ...pageRows.map((t, i) => `TEXT ${L.margin},${L.detailStart + L.lineGap * i},"${L.detailFont}",0,${L.detailMul},${L.detailMul},"${t}"`),
      "PRINT 1,1",
    );
  });
  out.push("");
  return out.join("\r\n");
}

// Self-test ribbon — confirms link, label dimensions, and layout scaling.
function buildTestPrintTSPL(size, fontOverrides) {
  const L = layoutFor(size.width, size.height, fontOverrides);
  const testData = "TEST-PRINT";
  const plan = planBarcodes(L, size.width, testData.length);
  return [
    ...sizeHeader(size),
    `TEXT ${L.margin},${L.titleY},"${L.titleFont}",0,${L.titleMul},${L.titleMul},"OZ BAKERY"`,
    `TEXT ${L.margin},${L.productY},"${L.productFont}",0,${L.productMul},${L.productMul},"Printer OK"`,
    `TEXT ${L.margin},${L.detailStart},"${L.detailFont}",0,${L.detailMul},${L.detailMul},"${new Date().toISOString().slice(0, 19)}"`,
    `TEXT ${L.margin},${L.detailStart + L.lineGap},"${L.detailFont}",0,${L.detailMul},${L.detailMul},"Label: ${size.width}x${size.height} mm"`,
    `TEXT ${L.margin},${L.detailStart + L.lineGap * 2},"${L.detailFont}",0,${L.detailMul},${L.detailMul},"Codes: ${plan.mode}"`,
    ...barcodeCommands(plan, L, testData),
    "PRINT 1,1",
    "",
  ].join("\r\n");
}

export function PrinterProvider({ children }) {
  const initialStatus = typeof navigator !== "undefined" && navigator.bluetooth
    ? "disconnected"
    : "unsupported";
  const [status, setStatus] = useState(initialStatus);
  const [deviceName, setDeviceName] = useState(null);
  const [lastError, setLastError] = useState(null);
  const [labelSize, setLabelSizeState] = useState(readStoredLabelSize);
  const [fontOverrides, setFontOverridesState] = useState(readStoredFontOverrides);

  // Update one or all of the manual font overrides. Pass `null` for any
  // role to clear it and fall back to the auto tier-based pick.
  const setLabelSize = useCallback((size) => {
    // Use Number.isFinite so an explicit 0 doesn't get clamped to the
    // default and an empty/NaN value gracefully falls back. Without
    // this an in-progress edit like clearing the input to retype would
    // snap the field back to the default mid-keystroke.
    const num = (v, dflt, min, max) => {
      const n = Number(v);
      if (!Number.isFinite(n) || n <= 0) return dflt;
      return Math.max(min, Math.min(max, n));
    };
    const next = {
      width: num(size.width, DEFAULT_LABEL_SIZE.width, 10, 200),
      height: num(size.height, DEFAULT_LABEL_SIZE.height, 10, 300),
      // Gap can legitimately be 0; treat NaN/empty as the default but
      // accept zero as the operator's actual choice.
      gap: (() => {
        const n = Number(size.gap);
        if (!Number.isFinite(n)) return DEFAULT_LABEL_SIZE.gap;
        return Math.max(0, Math.min(10, n));
      })(),
    };
    setLabelSizeState(next);
    try {
      localStorage.setItem(LABEL_SIZE_KEY, JSON.stringify(next));
    } catch (err) {
      devLog("[printer] label-size persistence failed:", err);
    }
  }, []);

  // Update one or all of the manual font overrides. Pass `null` for any
  // role to clear it and fall back to the auto tier-based pick.
  const setFontOverrides = useCallback((next) => {
    const sanitize = (v) => (v && FONT_BY_ID[v] ? v : null);
    const merged = {
      title: sanitize(next?.title ?? null),
      product: sanitize(next?.product ?? null),
      detail: sanitize(next?.detail ?? null),
    };
    setFontOverridesState(merged);
    try {
      localStorage.setItem(FONT_OVERRIDES_KEY, JSON.stringify(merged));
    } catch (err) {
      devLog("[printer] font-overrides persistence failed:", err);
    }
  }, []);

  // Refs hold the live BLE handles. They don't drive UI re-renders so they
  // sit outside React state — `status` + `deviceName` are the public face.
  const deviceRef = useRef(null);
  const characteristicRef = useRef(null);

  const handleDisconnect = useCallback(() => {
    characteristicRef.current = null;
    setStatus((s) => (s === "unsupported" ? s : "disconnected"));
    setDeviceName(null);
  }, []);

  // Walk candidate services until we find one with a writable characteristic.
  // Returns the characteristic or throws a diagnostic-rich error if no
  // candidate matched — we list which UUIDs *did* respond so we can extend
  // the candidate list to cover unknown firmwares.
  const findWritable = async (server) => {
    const foundServices = [];
    const foundChars = [];
    for (const svcUuid of CANDIDATE_SERVICES) {
      try {
        const svc = await server.getPrimaryService(svcUuid);
        foundServices.push(svcUuid);
        const chars = await svc.getCharacteristics();
        chars.forEach((c) => {
          foundChars.push({
            svc: svcUuid,
            char: c.uuid,
            props: Object.keys(c.properties).filter((k) => c.properties[k]),
          });
        });
        const writable = chars.find(
          (c) => c.properties.write || c.properties.writeWithoutResponse,
        );
        if (writable) return writable;
      } catch (err) {
        // Service not present on this firmware — expected control flow
        // when probing UUIDs. Log at debug so a long miss-chain is
        // diagnosable without spamming production toasts.
        devLog(`[printer] service ${svcUuid} not present:`, err?.name || err);
      }
    }
    // Build a diagnostic string so the operator can paste it to us and
    // we'll know exactly which UUID to add. Console too, for browser
    // dev-tools forensics.
    const diag = foundServices.length
      ? `Found services: ${foundServices.join(", ")} | chars: ${JSON.stringify(foundChars)}`
      : "No services from our candidate list were exposed by this device.";
    devLog("[printer] No writable characteristic.", { foundServices, foundChars });
    throw new Error(
      `No writable BLE characteristic found. ${diag} — share this with support so we can add the right UUID.`,
    );
  };

  const connect = useCallback(async () => {
    if (status === "unsupported") {
      toast.error("Web Bluetooth not supported on this browser. Use Chrome on Android.");
      return false;
    }
    setLastError(null);
    setStatus("connecting");
    try {
      const device = await navigator.bluetooth.requestDevice({
        // Accepting all devices keeps the chooser open for un-named clones.
        // Filtering by name prefix (e.g. "BY-") hides legitimate units that
        // ship with a different advertised name, so we cast a wide net.
        acceptAllDevices: true,
        optionalServices: CANDIDATE_SERVICES,
      });
      device.addEventListener("gattserverdisconnected", handleDisconnect);
      const server = await device.gatt.connect();
      const characteristic = await findWritable(server);
      deviceRef.current = device;
      characteristicRef.current = characteristic;
      setDeviceName(device.name || "Bluetooth printer");
      setStatus("connected");
      try {
        // Persist a hint so the chip can remember which device the bakery
        // last used. We only store the name; the actual permission grant
        // is browser-managed (Bluetooth permission per origin).
        localStorage.setItem(DEVICE_HINT_KEY, device.name || "");
      } catch (err) {
        devLog("[printer] device-hint persistence failed:", err);
      }
      toast.success(`Printer connected · ${device.name || "device"}`);
      return true;
    } catch (err) {
      const msg = err?.message || String(err);
      // The user dismissing the chooser is not an "error" we should beep at.
      const cancelled =
        msg.includes("User cancelled") || err?.name === "NotFoundError";
      setStatus("disconnected");
      setLastError(cancelled ? null : msg);
      if (!cancelled) toast.error(`Printer connect failed: ${msg}`);
      return false;
    }
  }, [status, handleDisconnect]);

  // Silent auto-reconnect: on mount, if the browser remembers a previously
  // permitted device we can attempt a GATT handshake WITHOUT prompting the
  // user. `navigator.bluetooth.getDevices()` (Chrome 85+ Android, behind a
  // flag on desktop) returns the origin's permitted devices; the user
  // grants that permission once via the visible +Connect flow above, and
  // every subsequent page load can just re-attach. Falls through silently
  // when the API is unavailable — the operator can still tap Connect.
  const attemptSilentReconnect = useCallback(async () => {
    if (status !== "disconnected") return false;
    if (typeof navigator === "undefined" || !navigator.bluetooth) return false;
    if (typeof navigator.bluetooth.getDevices !== "function") return false;
    try {
      const devices = await navigator.bluetooth.getDevices();
      if (!devices || devices.length === 0) return false;
      const storedHint = (() => {
        try {
          return localStorage.getItem(DEVICE_HINT_KEY) || "";
        } catch {
          return "";
        }
      })();
      // Prefer the last-connected device by name hint; fall back to first.
      const device =
        devices.find((d) => (d.name || "") && d.name === storedHint) ||
        devices[0];
      if (!device) return false;
      device.addEventListener("gattserverdisconnected", handleDisconnect);
      setStatus("connecting");
      const server = await device.gatt.connect();
      const characteristic = await findWritable(server);
      deviceRef.current = device;
      characteristicRef.current = characteristic;
      setDeviceName(device.name || "Bluetooth printer");
      setStatus("connected");
      // Deliberately no toast on silent reconnect — this happens on every
      // page load and would spam the operator. The green pill in the
      // header is signal enough.
      return true;
    } catch (err) {
      // Common failures: device out of range, powered off, or gatt.connect
      // blocked by browser needing a user gesture (some Chrome builds).
      // Log and stay disconnected; the +Connect button remains available.
      devLog("[printer] silent reconnect failed:", err?.message || err);
      setStatus("disconnected");
      return false;
    }
  }, [status, handleDisconnect]);

  // Fire the silent reconnect once on mount. We can't add it to `connect`'s
  // deps or it would loop when status flips.
  useEffect(() => {
    attemptSilentReconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const disconnect = useCallback(async () => {
    try {
      if (deviceRef.current?.gatt?.connected) {
        deviceRef.current.gatt.disconnect();
      }
    } catch (err) {
      // Disconnect is best-effort — the GATT layer may already be gone.
      devLog("[printer] disconnect cleanup error:", err);
    }
    deviceRef.current = null;
    handleDisconnect();
    toast.info("Printer disconnected");
  }, [handleDisconnect]);

  const writeTSPL = useCallback(async (commands) => {
    if (!characteristicRef.current) {
      toast.error("Printer not connected");
      return false;
    }
    try {
      await writeChunked(characteristicRef.current, encode(commands));
      return true;
    } catch (err) {
      setLastError(err?.message || String(err));
      toast.error(`Print failed: ${err?.message || err}`);
      return false;
    }
  }, []);

  const printPackingLabel = useCallback(
    (packing) => writeTSPL(buildPackingLabelTSPL(packing, labelSize, fontOverrides)),
    [writeTSPL, labelSize, fontOverrides],
  );

  const printIntakeLabel = useCallback(
    (intake) => writeTSPL(buildIntakeLabelTSPL(intake, labelSize, fontOverrides)),
    [writeTSPL, labelSize, fontOverrides],
  );

  const printRecipeLabel = useCallback(
    (recipe) => writeTSPL(buildRecipeLabelTSPL(recipe, labelSize, fontOverrides)),
    [writeTSPL, labelSize, fontOverrides],
  );

  const testPrint = useCallback(
    () => writeTSPL(buildTestPrintTSPL(labelSize, fontOverrides)),
    [writeTSPL, labelSize, fontOverrides],
  );

  // On unmount: drop the GATT handle so the printer doesn't stay attached
  // to a torn-down React tree (matters during hot reload + sign-out).
  useEffect(() => () => {
    if (deviceRef.current?.gatt?.connected) {
      try { deviceRef.current.gatt.disconnect(); } catch (err) {
        devLog("[printer] unmount disconnect error:", err);
      }
    }
  }, []);

  const value = useMemo(
    () => ({
      status,
      isConnected: isPrinter(status),
      deviceName,
      lastError,
      labelSize,
      setLabelSize,
      fontOverrides,
      setFontOverrides,
      connect,
      disconnect,
      printPackingLabel,
      printIntakeLabel,
      printRecipeLabel,
      testPrint,
    }),
    [status, deviceName, lastError, labelSize, setLabelSize, fontOverrides, setFontOverrides, connect, disconnect, printPackingLabel, printIntakeLabel, printRecipeLabel, testPrint],
  );

  return <PrinterCtx.Provider value={value}>{children}</PrinterCtx.Provider>;
}

export const usePrinter = () => useContext(PrinterCtx);
